import { Injectable } from '@angular/core';
import {
  HubConnectionBuilder,
  HubConnection,
  LogLevel,
  HubConnectionState,
  HttpTransportType,
} from '@microsoft/signalr';

import { BehaviorSubject } from 'rxjs';
import { SignalRLocalStorageService } from './signalr-local-storage.service';


/** Сервис для работы с SignalR */
@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  protected connection: HubConnection;

  private availableTransports = [HttpTransportType.WebSockets, HttpTransportType.LongPolling];

  private transportIndex = 0;

  private connectionAttemptCount = 0;
  private readonly connectionMaxAttemptCount = 100;

  constructor(private _localStorage: SignalRLocalStorageService) {}

  public init(serverDateBehaviorSubject: BehaviorSubject<any>, url: string) {
    this._localStorage.init(serverDateBehaviorSubject);

    this.transportIndex = this._localStorage.getPreferedTransportIndex(this.transportIndex);
    this.connection = new HubConnectionBuilder()
      .withUrl(url, this.availableTransports[this.transportIndex])
      .withAutomaticReconnect([0, 2000, 4000, 6000, 6000, 6000])
      .configureLogging(LogLevel.Information)
      .build();
  }

  public switchTransport(): boolean {
    if (this.transportIndex + 1 < this.availableTransports.length) {
      this.transportIndex++;
      this._localStorage.setPreferedTransportIndex(this.transportIndex);
      return true;
    }

    return false;
  }

  public getTransport() {
    return this.availableTransports[this.transportIndex];
  }

  public getConnectionState(): HubConnectionState {
    return this.connection?.state ?? HubConnectionState.Disconnected;
  }

  public isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  public isDisconnected(): boolean {
    return this.connection?.state === HubConnectionState.Disconnected;
  }

  public connect(): Promise<void> {
    this.connectionAttemptCount++;
    return this.connection.start();
  }

  public disconnect(): Promise<void> {
    return this.connection?.stop() ?? Promise.resolve();
  }

  public getConnectionId(): string {
    return this.connection.connectionId;
  }

  public setOnCloseCallback(callback: (error?: Error) => void): void {
    this.connection.onclose(callback);
  }

  public setOnReconnectingCallback(callback: (error?: Error) => void): void {
    this.connection.onreconnecting(callback);
  }

  public setOnReconnectedCallback(callback: (connectionId?: string) => void): void {
    this.connection.onreconnected(callback);
  }

  public setMethodCallback(methodName: string, callback: (...args: any[]) => any): void {
    this.connection.on(methodName, callback);
  }

  public invoke(methodName: string, ...args: any[]): Promise<any> {
    return this.connection.invoke(methodName, ...args);
  }

  public isConnectionMaxAttemptCountReached(){
    return this.connectionAttemptCount >= this.connectionMaxAttemptCount;
  }
}
